import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { getGlassClasses } from '../../../utils/theme';

/**
 * GlassCard Component
 *
 * A glassmorphic card component with Synap branding.
 * Perfect for displaying content with elegant glass effects.
 */

const cardVariants = cva(
  'rounded-xl transition-all duration-200',
  {
    variants: {
      variant: {
        glass: getGlassClasses('default'),
        'glass-strong': getGlassClasses('strong'),
        'glass-subtle': getGlassClasses('subtle'),
        'glass-medium': getGlassClasses('default'),
        'glass-accent': cn(getGlassClasses('default'), 'border-accent-400/20 bg-accent-500/[0.06]'),
        'glass-primary': cn(getGlassClasses('default'), 'border-primary-400/20 bg-primary-500/[0.06]'),
        'glass-error': cn(getGlassClasses('default'), 'border-red-400/20 bg-red-500/[0.06]'),
        error: cn(getGlassClasses('default'), 'border-red-400/20 bg-red-500/[0.06]'),
        solid: 'bg-[#0C1319] border border-white/[0.06]',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
      hover: {
        none: '',
        lift: 'hover:bg-white/[0.06] hover:border-white/[0.1]',
        glow: 'hover:border-primary-400/20',
      },
    },
    defaultVariants: {
      variant: 'glass',
      padding: 'md',
      hover: 'none',
    },
  }
);

export interface GlassCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /** Card content */
  children: React.ReactNode;
  /** Optional header content */
  header?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
}

export const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  (
    {
      className,
      variant,
      padding,
      hover,
      children,
      header,
      footer,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn(cardVariants({ variant, padding, hover, className }))}
        ref={ref}
        {...props}
      >
        {header && (
          <div className="border-b border-white/[0.06] pb-4 mb-4">
            {header}
          </div>
        )}
        <div>{children}</div>
        {footer && (
          <div className="border-t border-white/[0.06] pt-4 mt-4">
            {footer}
          </div>
        )}
      </div>
    );
  }
);

GlassCard.displayName = 'GlassCard';

/**
 * GlassCardHeader Component
 */
export interface GlassCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const GlassCardHeader = React.forwardRef<HTMLDivElement, GlassCardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col space-y-1.5', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

GlassCardHeader.displayName = 'GlassCardHeader';

/**
 * GlassCardTitle Component
 */
export interface GlassCardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

export const GlassCardTitle = React.forwardRef<HTMLHeadingElement, GlassCardTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

GlassCardTitle.displayName = 'GlassCardTitle';

/**
 * GlassCardDescription Component
 */
export interface GlassCardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

export const GlassCardDescription = React.forwardRef<HTMLParagraphElement, GlassCardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-white/70 dark:text-white/60', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

GlassCardDescription.displayName = 'GlassCardDescription';

/**
 * GlassCardContent Component
 */
export interface GlassCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const GlassCardContent = React.forwardRef<HTMLDivElement, GlassCardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('pt-0', className)} {...props}>
        {children}
      </div>
    );
  }
);

GlassCardContent.displayName = 'GlassCardContent';

/**
 * GlassCardFooter Component
 */
export interface GlassCardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const GlassCardFooter = React.forwardRef<HTMLDivElement, GlassCardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center pt-0', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

GlassCardFooter.displayName = 'GlassCardFooter';
